import React from 'react';
import { When } from 'react-if';
import PropTypes from 'prop-types';
import { useTranslation, Trans } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Spacing, Text, Icon, ProviderLogo, Box, MessageBox } from '@reservamos/elements';
import emojiParty from 'images/icons/emoji-party.png';
import SendEmail from 'components/purchase/SendEmail';
import 'styles/components/purchase/PurchasesLayout';
import PurchaseToken from 'ui/atoms/PurchaseToken';
import ReserveCode from 'ui/atoms/ReserveCode';
import MinorCompleteDisclaimer from 'components/purchase/MinorCompleteDisclaimer';
import useWhitelabelFeatures from 'hooks/whitelabel/useWhitelabelFeatures';
import usePurchase from 'hooks/store/usePurchase';
import {
  purchaseHasFreeFarePassenger,
  purchaseIsAllFreeFareTickets,
} from 'utils/freeFareDocumentRules';
import useSeatsBus from 'hooks/store/useSeatsBus';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import useShowPricesByGroup from 'hooks/useShowPricesByGroup';
import PassengerTicketsList from 'ui/molecules/PassengerTicketsList';
import { getSeatPriceZonesForPassengersLeg } from 'utils/seatPricing';
import PaymentStepStatus from '../../components/purchase/PaymentStepStatus/PaymentStepStatus';
import TicketSummary from './TicketSummary';
import PaymentStatusIcon from '../../components/purchase/PaymentStatusIcon';
import PoliciesContainer from './PoliciesContainer';
import AdjacentSeatCard from '../atoms/AdjacentSeatCard';
import { hasAdjacentSeatsInTickets } from '../../utils/adjacentCard';
import TravelTipsMessage from '../../components/purchase/TravelTipsMessage';
import PurchaseCompleteBanners from '../../components/purchase/PurchaseCompleteBanners';

/**
 * DownloadTickets Component
 *
 * Component for displaying downloadable tickets with associated information such as departure and return details, payment status, and email instructions. Utilizes child components like TicketSummary and PaymentStatusIcon.
 *
 * @component
 * @param {string} hrefDeparture - The URL for downloading the departure ticket.
 * @param {string} hrefReturn - The URL for downloading the return ticket.
 * @param {string} instructions - Additional instructions or information.
 * @param {boolean} isReturnOpen - Indicates whether the return ticket information is open.
 * @param {boolean} isDepartureOpen - Indicates whether the departure ticket information is open.
 * @param {Object} status - Object containing payment and ticket status information.
 * @param {string} email - The email associated with the ticket.
 * @param {string} departsTransporterKey - Transporter key for departure.
 * @param {string} returnsTransporterKey - Transporter key for return.
 * @param {string} departsReserveCode - Reserve code for departure.
 * @param {string} returnsReserveCode - Reserve code for return.
 * @param {boolean} displayDownloadTicketsButton - Indicates whether to display the download tickets button.
 * @param {string} departureDate - The date of departure.
 * @param {string} departureOriginTime - The departure time for the origin.
 * @param {string} departureOriginTerminal - The departure terminal for the origin.
 * @param {string} departureOriginDirections - The boarding gate inside the departure origin terminal.
 * @param {string} departureOriginCity - The city of departure for the origin.
 * @param {string} departureOriginLocation - The location link for departure origin.
 * @param {string} departureDestinationTime - The arrival time for the destination.
 * @param {string} departureDestinationTerminal - The arrival terminal for the destination.
 * @param {string} departureDestinationDirections - The boarding gate inside the departure destination terminal.
 * @param {string} departureDestinationCity - The city of arrival for the destination.
 * @param {string} departureDestinationLocation - The location link for departure destination.
 * @param {string} returnDate - The date of return.
 * @param {string} returnOriginTime - The departure time for the return origin.
 * @param {string} returnOriginTerminal - The departure terminal for the return origin.
 * @param {string} returnOriginDirections - The boarding gate inside the return origin terminal.
 * @param {string} returnOriginCity - The city of departure for the return origin.
 * @param {string} returnOriginLocation - The location link for return origin.
 * @param {string} returnDestinationTime - The arrival time for the return destination.
 * @param {string} returnDestinationTerminal - The arrival terminal for the return destination.
 * @param {string} returnDestinationDirections - The boarding gate inside the return destination terminal.
 * @param {string} returnDestinationCity - The city of arrival for the return destination.
 * @param {string} returnDestinationLocation - The location link for return destination.
 * @param {string} token - The purchase token.
 * @param {Array} departsTickets - Array of departure tickets.
 * @param {Array} returnsTickets - Array of return tickets.
 *
 * @returns {JSX.Element} - The rendered DownloadTickets component.
 */

const DownloadTickets = ({
  hrefDeparture,
  hrefReturn,
  instructions,
  isReturnOpen,
  isDepartureOpen,
  email,
  status,
  departsTransporterKey,
  returnsTransporterKey,
  departsReserveCode,
  returnsReserveCode,
  displayDownloadTicketsButton,
  departureDate,
  departureOriginTime,
  departureOriginTerminal,
  departureOriginDirections,
  departureOriginCity,
  departureOriginLocation,
  departureDestinationTime,
  departureDestinationTerminal,
  departureDestinationDirections,
  departureDestinationCity,
  departureDestinationLocation,
  returnDate,
  returnOriginTime,
  returnOriginTerminal,
  returnOriginDirections,
  returnOriginCity,
  returnOriginLocation,
  returnDestinationTime,
  returnDestinationTerminal,
  returnDestinationDirections,
  returnDestinationCity,
  returnDestinationLocation,
  token,
  departsTickets,
  returnsTickets,
}) => {
  const {
    TICKETS_REFERENCE,
    HIDE_LOCATION_ON_PURCHASE_COMPLETE,
    BULK_TICKET_ENABLED,
    PROMOTIONAL_CODE_ENABLED,
    SHOW_RESERVE_CODE,
    FREE_FARE_ATTENTION,
  } = useWhitelabelFeatures();
  const { brand } = useWhitelabelEnvs();
  const { t } = useTranslation(['purchase', 'seats']);
  const purchase = usePurchase();
  const {
    bulkTicket: isBulkTicket,
    departs,
    returns,
    passengers,
    promoTravelerCode,
    needsMinorInfoAfterComplete,
  } = purchase;
  const departureFragment = departs?.fragments?.[0] ?? null;
  const seatsBus = useSeatsBus();
  const applySeatZones = useShowPricesByGroup();
  const departsSeatPriceZones = getSeatPriceZonesForPassengersLeg(departs || {}, seatsBus);
  const returnsSeatPriceZones = getSeatPriceZonesForPassengersLeg(returns || {}, seatsBus);
  const isConecta = BULK_TICKET_ENABLED;
  const lines = useSelector((state) => state.lines);
  const showFreeFareAttention = FREE_FARE_ATTENTION && purchaseHasFreeFarePassenger(purchase);
  const hidePurchaseOrderReferenceUi = purchaseIsAllFreeFareTickets(purchase);

  const displayPurchaseToken = TICKETS_REFERENCE === 'purchase-token' && !SHOW_RESERVE_CODE;
  // TODO: Update validation to hide buttons if coordinates are invalid (for example: undefined or 0,0)
  const hideViewLocation = HIDE_LOCATION_ON_PURCHASE_COMPLETE;
  const displayTransporterKey =
    TICKETS_REFERENCE === 'transporter-key' && brand === 'cds' && !SHOW_RESERVE_CODE;

  const shouldShowReserveCode = SHOW_RESERVE_CODE;
  const hasReserveCodes = departsReserveCode || returnsReserveCode;

  const displayPurchaseTokenFallback =
    shouldShowReserveCode && !hasReserveCodes && token && !departsTransporterKey;
  const displayTransporterKeyFallback =
    shouldShowReserveCode && !hasReserveCodes && departsTransporterKey;

  const displayReserveCode =
    shouldShowReserveCode && hasReserveCodes ? departsReserveCode || returnsReserveCode : null;
  const purchaseType = returnOriginCity ? 'round_trip' : 'one_way';
  const isOpenTicket = Boolean(isDepartureOpen || isReturnOpen);

  return (
    <Spacing vertical size="XXL">
      <div className="purchase-layout-content-wrapper">
        <Spacing vertical>
          <Spacing vertical size="XS">
            <Text weight="bold" size="XL">
              {t('label.tickets_ready')}
              <Icon type={emojiParty} hasMargin marginSide="left" />
            </Text>
            <Text color="grayMedium">{instructions}</Text>
          </Spacing>
          {isConecta && !isBulkTicket && (
            <ProviderLogo
              name={departureFragment?.line.name}
              imgSrc={departureFragment?.line.logoUrl}
            />
          )}
          {isBulkTicket && lines && (
            <Spacing size="S" alignItems="center">
              {Object.values(lines).map((line) => {
                return (
                  <Box elementType="span" paddingHorizontal="XS" paddingVertical="XS">
                    <img key={line.name} alt={line.name} src={line.logoUrl} className="w-28" />
                  </Box>
                );
              })}
            </Spacing>
          )}
          {!hidePurchaseOrderReferenceUi && token && !SHOW_RESERVE_CODE && (
            <PurchaseToken
              token={displayTransporterKey ? departsTransporterKey : token}
              displayTransporterKey={displayTransporterKey}
            />
          )}
          {!hidePurchaseOrderReferenceUi && displayPurchaseTokenFallback && (
            <PurchaseToken token={token} displayTransporterKey={false} />
          )}
          {!hidePurchaseOrderReferenceUi && displayTransporterKeyFallback && (
            <PurchaseToken token={departsTransporterKey} displayTransporterKey />
          )}
          {isBulkTicket && (
            <PoliciesContainer customTitle={t('seats:passenger_policy.reminder')}>
              <Text size="XS" color="grayMedium" elementType="span">
                &#8226; {t('seats:passenger_policy.availability_subject')}{' '}
                <Text size="XS" weight="bold" elementType="span">
                  {t('seats:passenger_policy.availability_subject_recommendation')}
                </Text>
              </Text>
            </PoliciesContainer>
          )}
          {needsMinorInfoAfterComplete && <MinorCompleteDisclaimer />}
        </Spacing>
        <PaymentStatusIcon />
      </div>

      <When condition={displayDownloadTicketsButton}>
        <Spacing vertical>
          <Spacing size="XS" alignItems="center">
            <When condition={displayTransporterKey && !hidePurchaseOrderReferenceUi}>
              <PurchaseToken
                token={displayTransporterKey ? departsTransporterKey : token}
                displayTransporterKey={displayTransporterKey}
              />
            </When>
            <When condition={displayReserveCode && !hidePurchaseOrderReferenceUi}>
              <ReserveCode reserveCode={displayReserveCode} />
            </When>
          </Spacing>
          <When condition={PROMOTIONAL_CODE_ENABLED && promoTravelerCode}>
            <Text color="grayMedium">
              <Trans
                t={t}
                i18nKey="purchase:promo_traveler_code"
                values={{ promoTravelerCode }}
                components={{
                  1: <Text color="success" elementType="span" />,
                }}
              />
            </Text>
          </When>
          <TicketSummary
            title={t(!isDepartureOpen ? 'ticket_departure' : 'ticket_departure_open')}
            date={departureDate}
            isOpen={isDepartureOpen}
            originTime={departureOriginTime}
            originTerminal={departureOriginTerminal}
            originDirections={departureOriginDirections}
            originCity={departureOriginCity}
            originLocation={hideViewLocation ? '' : departureOriginLocation}
            destinationTime={departureDestinationTime}
            destinationTerminal={departureDestinationTerminal}
            destinationDirections={departureDestinationDirections}
            destinationCity={departureDestinationCity}
            destinationLocation={hideViewLocation ? '' : departureDestinationLocation}
            buttonLabel={t(!isDepartureOpen ? 'get_ticket' : 'get_ticket_open')}
            href={hrefDeparture}
          />
          <When condition={displayPurchaseToken && !hidePurchaseOrderReferenceUi}>
            <Text size="S">
              {`${t('purchase_reference')} `}
              <Text size="S" elementType="span" weight="bold">
                {departsTransporterKey}
              </Text>
            </Text>
          </When>
          <PassengerTicketsList
            tickets={departsTickets}
            passengers={passengers}
            purchaseToken={token}
            areOpenTickets={isDepartureOpen}
            applySeatZones={applySeatZones}
            seatPriceZones={departsSeatPriceZones}
            selectedSeats={departs?.selectedSeats || []}
          />
          {returnOriginCity && (
            <>
              <When condition={displayTransporterKey && !hidePurchaseOrderReferenceUi}>
                <PurchaseToken token={returnsTransporterKey} displayTransporterKey isRound />
              </When>
              <TicketSummary
                title={t(!isReturnOpen ? 'ticket_return' : 'ticket_return_open')}
                date={returnDate}
                isOpen={isReturnOpen}
                originTime={returnOriginTime}
                originTerminal={returnOriginTerminal}
                originDirections={returnOriginDirections}
                originCity={returnOriginCity}
                originLocation={hideViewLocation ? '' : returnOriginLocation}
                destinationTime={returnDestinationTime}
                destinationTerminal={returnDestinationTerminal}
                destinationDirections={returnDestinationDirections}
                destinationCity={returnDestinationCity}
                destinationLocation={hideViewLocation ? '' : returnDestinationLocation}
                buttonLabel={t(!isReturnOpen ? 'get_ticket' : 'get_ticket_open')}
                href={hrefReturn}
              />
              <When condition={displayPurchaseToken && !hidePurchaseOrderReferenceUi}>
                <Text size="S">
                  {`${t('purchase_reference')} `}
                  <Text size="S" elementType="span" weight="bold">
                    {returnsTransporterKey}
                  </Text>
                </Text>
              </When>

              <PassengerTicketsList
                tickets={returnsTickets}
                passengers={passengers}
                purchaseToken={token}
                areOpenTickets={isReturnOpen}
                applySeatZones={applySeatZones}
                seatPriceZones={returnsSeatPriceZones}
                selectedSeats={returns?.selectedSeats || []}
              />
            </>
          )}
          <AdjacentSeatCard
            isVisible={hasAdjacentSeatsInTickets([
              ...(departsTickets || []),
              ...(returnsTickets || []),
            ])}
            hasToggleSwitch={false}
            id="adjacent-seat-info"
            checked={false}
            isLoading={false}
            onChange={() => {}}
            title={t('seats:adjacent_seat.title')}
            text={t('seats:adjacent_seat.message')}
          />
          <TravelTipsMessage />
        </Spacing>
      </When>
      {showFreeFareAttention && (
        <MessageBox
          borderColor="warning"
          borderRadius="L"
          iconType="Warning"
          iconSize="M"
          titleIcon
          title={t('purchase:free_fare_attention.title')}
          widthFull
        >
          <Text size="S">{t('purchase:free_fare_attention.body')}</Text>
        </MessageBox>
      )}
      <Spacing flexGrow isResponsive>
        <Spacing vertical size="S">
          <PaymentStepStatus
            transporterType="bus"
            status={status.sent}
            title="sent"
            email={email}
          />
          <Text size="S">{t('payment:text.check_spam_email')}</Text>
        </Spacing>
        <SendEmail />
      </Spacing>
      <PurchaseCompleteBanners purchaseType={purchaseType} isOpenTicket={isOpenTicket} />
    </Spacing>
  );
};

DownloadTickets.propTypes = {
  hrefDeparture: PropTypes.string,
  hrefReturn: PropTypes.string,
  instructions: PropTypes.string,
  isReturnOpen: PropTypes.bool,
  isDepartureOpen: PropTypes.bool,
  status: PropTypes.shape({
    payment: PropTypes.string.isRequired,
    tickets: PropTypes.string.isRequired,
    sent: PropTypes.string.isRequired,
  }).isRequired,
  email: PropTypes.string,
  departsTransporterKey: PropTypes.string,
  returnsTransporterKey: PropTypes.string,
  departsReserveCode: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  returnsReserveCode: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  displayDownloadTicketsButton: PropTypes.bool,
  departureDate: PropTypes.string.isRequired,
  departureOriginTime: PropTypes.string,
  departureOriginTerminal: PropTypes.string.isRequired,
  departureOriginDirections: PropTypes.string,
  departureOriginCity: PropTypes.string.isRequired,
  departureOriginLocation: PropTypes.string.isRequired,
  departureDestinationTime: PropTypes.string.isRequired,
  departureDestinationTerminal: PropTypes.string.isRequired,
  departureDestinationDirections: PropTypes.string,
  departureDestinationCity: PropTypes.string.isRequired,
  departureDestinationLocation: PropTypes.string.isRequired,
  returnDate: PropTypes.string,
  returnOriginTime: PropTypes.string,
  returnOriginTerminal: PropTypes.string,
  returnOriginDirections: PropTypes.string,
  returnOriginCity: PropTypes.string,
  returnOriginLocation: PropTypes.string,
  returnDestinationTime: PropTypes.string,
  returnDestinationTerminal: PropTypes.string,
  returnDestinationDirections: PropTypes.string,
  returnDestinationCity: PropTypes.string,
  returnDestinationLocation: PropTypes.string,
  token: PropTypes.string,
  departsTickets: PropTypes.array,
  returnsTickets: PropTypes.array,
};

DownloadTickets.defaultProps = {
  hrefDeparture: '',
  hrefReturn: '',
  instructions: '',
  email: '',
  departureOriginTime: '',
  departureOriginDirections: '',
  departureDestinationDirections: '',
  returnOriginTime: '',
  returnOriginTerminal: '',
  returnOriginDirections: '',
  returnOriginCity: '',
  returnOriginLocation: '',
  returnDestinationTime: '',
  returnDestinationTerminal: '',
  returnDestinationDirections: '',
  returnDestinationCity: '',
  returnDestinationLocation: '',
  token: false,
  departsTickets: [],
  returnsTickets: [],
};
export default DownloadTickets;
